import classNames from 'classnames';
import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useHistory } from 'react-router-dom';
import { TEAM_API_BASE, WBS_ELEMENT_BASE_URL } from '../../../../api/api';
import { useGenericAPI } from '../../../../api/useGenericApi';
import { ProIconButton } from '../../../../atoms/Button/ProIconButton';
import { ActionCell } from '../../../../atoms/Cells/ActionCell';
import { ProIconData, ProIconDataButtons } from '../../../../atoms/Icons/IconData';
import { ProStatus } from '../../../../atoms/ProjectStatus/ProStatus';
import { ProTable, TableColData } from '../../../../atoms/Table/Table';
import { ApplicationElementType, getBaseBCItemForType } from '../../../../components/DetailWindow/breadCrump.helpers';
import { ProPage } from '../../../../components/Page/Page';
import { ProjectContext } from '../../../../contexts/ProjectContext';
import { WBSContext } from '../../../../contexts/WBSContext';
import { proLinkStructure } from '../../../../modules/Menu/Navigate';
import { getIconForTypeInNameCell } from '../../../../modules/Windows/WBSElement/WBSElement.helpers';
import { ProIntent } from '../../../../utils/enums';
import { getPersonsName } from '../../../../utils/helpers';
import { Components } from '../../../../utils/types';
import { usePersonalWBSElemDetailFrame } from '../../WorkPackages/WorkPackage.helpers';

export const SCMeetingTree = () => {
    const { t } = useTranslation();
    const [selectedTableRow, setSelectedTableRow] = useState<number | undefined>();
    const [selectedElement, setSelectedElement] = useState<Components.Schemas.WBSElementDeltaDTO>();

    const history = useHistory();

    const navigate = (elem: Components.Schemas.WBSElementDeltaDTO) => {
        const to = generatePath(proLinkStructure.teamManagement.ceremonies.steering.path, {
            wbsElementId: elem.id,
            deltaId: elem.wbsElementDelta?.id,
        });
        // console.log('to', to, 'obj', {
        //     wbsElementId: elem.id,
        //     deltaId: elem.wbsElementDelta?.id,
        // });
        history.push(to);
    };

    const navigateToTeam = () => {
        history.push(proLinkStructure.teamManagement.ceremonies.steering.teampath);
    };

    const { selectedWBSStructureId, selectedDeltaWBSStructureId } = useContext(WBSContext);
    const { selectedTeam } = useContext(ProjectContext);
    const {
        data: wbsElements,
        error,
        loading,
        sync,
    } = useGenericAPI<Components.Schemas.WBSElementDeltaDTO[]>(
        `${WBS_ELEMENT_BASE_URL}${TEAM_API_BASE}/${selectedTeam?.id}/Baseline/${selectedWBSStructureId}/Delta`,
        selectedDeltaWBSStructureId
    );

    const deselectTableRow = () => {
        setSelectedTableRow(undefined);
        setSelectedElement(undefined);
        openWBSElement('hidden');
    };

    const selectTableRow = (i: number) => {
        setSelectedTableRow(i);
        setSelectedElement(wbsElements ? wbsElements[i] : undefined);
        openWBSElement('view');
    };

    const {
        data: detailWbsElement,
        isDisplayed,
        loading: detailLoading,
        mode: WBSDetailMode,
        open: openWBSElement,
        renderDetailWindow,
        sync: detailSync,
    } = usePersonalWBSElemDetailFrame(deselectTableRow, selectedElement?.id, sync);

    const tableData: TableColData[] = [
        {
            name: 'WBS Element',
            cellRender: (i) => {
                const item = wbsElements && wbsElements[i];
                if (item) {
                    return (
                        <>
                            <div className="ProTable__levelBars ">
                                <div
                                    className={classNames(
                                        'ProTable__levelBar',
                                        i === 0 ? `ProTable__levelBar-1` : `ProTable__levelBar-2`
                                    )}
                                >
                                    0
                                </div>
                            </div>
                            <span className="ProTable__name" onClick={() => selectTableRow(i)}>
                                {item.wbsElementType && getIconForTypeInNameCell(item.wbsElementType)}
                                {item.name}
                                {item.humanResourceAssignments && item.humanResourceAssignments[0]
                                    ? ' - ' + item.humanResourceAssignments[0].humanResource?.teamPosition?.name
                                    : ''}
                            </span>
                        </>
                    );
                }
            },
            className: 'TableRowName ProTable__levels',
            colSpace: 'extend',
        },

        {
            name: 'Time',
            cellRender: (i) => {
                const item = wbsElements && wbsElements[i];
                return (
                    <ProStatus
                        percent={item?.deltaTimePercent}
                        state={item?.deltaTimeState}
                        icon={ProIconData.time_fill}
                    />
                );
            },
            className: 'ProTable__status',
            colSpace: 2,
        },
        {
            name: 'Cost',
            cellRender: (i) => {
                const item = wbsElements && wbsElements[i];
                return (
                    <ProStatus
                        percent={item?.deltaCostPercent}
                        state={item?.deltaCostState}
                        icon={ProIconData.cost_fill}
                    />
                );
            },
            className: 'ProTable__status',
            colSpace: 2,
        },
        {
            name: 'Quality',
            cellRender: (i) => {
                const item = wbsElements && wbsElements[i];
                return (
                    <ProStatus
                        percent={item?.deltaQualityPercent}
                        state={item?.deltaQualityState}
                        icon={ProIconData.quality_fill}
                    />
                );
            },
            className: 'ProTable__status',
            colSpace: 2,
        },
        {
            name: 'Person',
            cellRender: (i) => {
                return wbsElements && getPersonsName(t, wbsElements[i].responsiblePerson);
            },
            hidden: () => !isDisplayed,
            colSpace: 3,
        },
        {
            name: t('table.cols.action'),
            cellRender: (i) => {
                const item = wbsElements && wbsElements[i];
                if (item) {
                    return (
                        <ActionCell
                            i={i}
                            selectTableRow={
                                i === 0
                                    ? undefined
                                    : () => {
                                          selectTableRow(i);
                                      }
                            }
                            detailWindowMode={[WBSDetailMode]}
                        >
                            <ProIconButton
                                icon={ProIconDataButtons.two_check_s}
                                onClick={() => (i === 0 ? navigateToTeam() : navigate(item))}
                                tooltip={'Push'}
                                mainIntent={ProIntent.Transparent}
                                hoverIntent={ProIntent.Success}
                                lineIntent={ProIntent.Create}
                                placement="bottom"
                            />
                        </ActionCell>
                    );
                }
            },
            colSpace: 4,
        },
    ];

    return (
        <ProPage
            breadCrump={[
                ...getBaseBCItemForType([ApplicationElementType.Team], t),
                { name: selectedTeam?.name ?? '-' },
            ]}
            name={'Steering Committee Meeting'}
            icon={ProIconData.package_fill}
            isHidden={false}
            displayProjectSelector
            loading={loading}
            displyTeamSelector
        >
            <ProTable
                data={tableData}
                rowsCount={wbsElements?.length ?? 1}
                selectedTableRow={selectedTableRow}
                getRowClass={(i) => (i === 0 ? 'ProTable--row__level-0' : 'ProTable--row__level-1')}
            />
            <div
                className={classNames(
                    'ProApp--detailWindowSection',
                    isDisplayed && 'ProApp--detailWindowSection__expanded'
                )}
            >
                {renderDetailWindow()}
            </div>
        </ProPage>
    );
};
